import find from 'lodash/find';
import map from 'lodash/map';
import filter from 'lodash/filter';
import isEmpty from 'lodash/isEmpty';
import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import IconCheck from '@spothero/icons/check';
import IconExclamationCircle from '@spothero/icons/exclamation-circle';
import Alert from '@spothero/ui/alert';
import FormElementError from '@spothero/ui/form-element-error';
import FormGroup from '@spothero/ui/form-group';
import FormGroupError from '@spothero/ui/form-group-error';
import Label from '@spothero/ui/label';
import Loader from '@spothero/ui/loader';
import Radio from '@spothero/ui/radio';
import RadioGroup from '@spothero/ui/radio-group';
import TextButton from '@spothero/ui/text-button';
import Tooltip from '@spothero/ui/tooltip';
import Price from 'common/price/Price';
import APIUtils from '@spothero/utils/api';
import withConfig from 'config/withConfig';
import OversizedFeeAPI from 'api/oversized-fee';
import AddNewVehicleForm from './AddNewVehicleForm';

class VehicleList extends Component {
    static propTypes = {
        isMobile: PropTypes.bool.isRequired,
        user: PropTypes.object,
        facilityId: PropTypes.number,
        vehicleId: PropTypes.number,
        make: PropTypes.string,
        model: PropTypes.string,
        year: PropTypes.number,
        edit: PropTypes.bool,
        isParentVisible: PropTypes.bool,
        required: PropTypes.bool,
        checkOversized: PropTypes.bool,
        feeMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
        defaultFeeMessage: PropTypes.string,
        iDontKnowChecked: PropTypes.bool,
        vehicleNotAllowed: PropTypes.bool,
        showUnknownChoices: PropTypes.bool,
        showSaveAsDefault: PropTypes.bool,
        loadVehicleDataOnMount: PropTypes.bool,
        allowManualEntry: PropTypes.bool,
        newVehicleFormErrors: PropTypes.array,
        onOversizedFeeUpdated: PropTypes.func,
        onIDontKnowChanged: PropTypes.func,
        onShowAddNew: PropTypes.func,
        onHideAddNew: PropTypes.func,
        currencyType: PropTypes.string,
        supportsOversizeFeeType: PropTypes.bool,
        oversizeFee: PropTypes.object,
        onVehicleChange: PropTypes.func,
        isLoadingRates: PropTypes.bool,
        isNewVehicle: PropTypes.bool,
    };
    static defaultProps = {
        edit: true,
        required: true,
        defaultFeeMessage: 'Oversized fees',
        showUnknownChoices: true,
        showSaveAsDefault: true,
    };

    constructor(props) {
        super(props);

        const {
            edit,
            iDontKnowChecked,
            feeMessage,
            defaultFeeMessage,
            vehicleNotAllowed,
            user,
            showUnknownChoices,
            vehicleId,
            make,
            model,
            year,
        } = props;
        const {
            vehicleId: newVehicleId,
            make: newMake,
            model: newModel,
            year: newYear,
            vehicles,
            showAddNew,
        } = this._getUserVehicles({
            user,
            showUnknownChoices,
            iDontKnowChecked,
            vehicleId,
            make,
            model,
            year,
        });

        this._requiredErrorMessage = 'Vehicle selection is required.';
        this._yearsByModel = [];

        this.state = {
            vehicles,
            vehicleId: newVehicleId,
            make: newMake,
            model: newModel,
            year: newYear,
            edit,
            iDontKnowChecked,
            feeMessage,
            requiredError: null,
            showWarningTooltip: false,
            highlight: false,
            fee: null,
            feeError: null,
            apiFailed: false,
            feeMessageLoading: false,
            defaultFeeMessage,
            vehicleNotAllowed,
            showAddNew,
            isNewVehicleValid: false,
        };
    }

    componentDidMount() {
        const {loadVehicleDataOnMount, checkOversized} = this.props;

        if (loadVehicleDataOnMount) {
            const {make, model, year} = this.state;

            if (!make && !model && !year) {
                return;
            }

            if (checkOversized) {
                this._loadOversizedFee({make, model, year});
            }
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const {showAddNew} = this.state;
        const {showAddNew: prevShowAddNew} = prevState;

        if (prevShowAddNew && !showAddNew) {
            const {onHideAddNew} = this.props;

            if (onHideAddNew) {
                onHideAddNew();
            }
        }
    }

    componentWillUnmount() {
        this._isUnmounted = true;

        if (this._feeLoader) {
            this._feeLoader.cancel();
        }
    }

    _onWarningTooltipHidden = () => {
        this.setState({
            showWarningTooltip: false,
        });
    };

    _onNewVehicleSelectChange = ({make, model, year}) => {
        this.setState({
            make,
            model,
            year,
            requiredError: null,
        });
    };

    _onVehicleChange = evt => {
        const {onShowAddNew, checkOversized, onVehicleChange} = this.props;
        const {vehicles} = this.state;
        const value = evt.target.value;
        const selectedVehicle = find(vehicles, {value});

        if (value === 'DONTKNOW') {
            this._onIDontKnowSelected();
        } else if (value === 'new') {
            this.setState({
                vehicleId: null,
                make: null,
                model: null,
                year: null,
                iDontKnowChecked: false,
                fee: null,
                feeMessage: null,
                feeError: null,
                showAddNew: true,
                showWarningTooltip: false,
                isNewVehicleValid: false,
            });

            if (onShowAddNew) {
                onShowAddNew();
            }
        } else if (checkOversized) {
            const {make, model, year} = selectedVehicle;

            this._loadOversizedFee({
                make,
                model,
                year,
            });
        } else {
            const {make, model, year} = selectedVehicle;

            this.setState({
                showAddNew: false,
                vehicleId: value,
                make,
                model,
                year,
                isNewVehicleValid: false,
                showWarningTooltip: false,
            });
        }

        if (onVehicleChange) {
            onVehicleChange(selectedVehicle);
        }
    };

    _onVehicleDataRetrieved = data => {
        const {checkOversized, supportsOversizeFeeType} = this.props;
        const {id, make, model, year} = data;
        const newState = data;

        if (make || model || year) {
            newState.iDontKnowChecked = false;
        }

        if (make && model && year) {
            if (checkOversized) {
                this._loadOversizedFee(data, false);
            } else if (supportsOversizeFeeType) {
                this._onNewVehicleChange({make, model, year, id});
            }
        } else {
            newState.fee = null;
        }

        this.setState(newState);
    };

    _onIDontKnowSelected() {
        const {onOversizedFeeUpdated, checkOversized} = this.props;
        const newState = {
            vehicleId: null,
            make: null,
            model: null,
            year: null,
            iDontKnowChecked: true,
            fee: null,
            feeMessage: null,
            feeError: null,
            requiredError: null,
            highlight: false,
            showAddNew: false,
            isNewVehicleValid: false,
            showWarningTooltip: true,
        };

        this.setState(newState);

        if (checkOversized && onOversizedFeeUpdated) {
            onOversizedFeeUpdated({
                response: false,
                state: newState,
            });
        }
    }

    _onEditClick = evt => {
        evt.preventDefault();

        this.setState({
            edit: true,
        });
    };

    _onNewVehicleChange = ({make, model, year, id}) => {
        const {onVehicleChange} = this.props;

        if (onVehicleChange) {
            onVehicleChange({
                isNew: true,
                make,
                model,
                year,
                value: id,
            });
        }

        this.setState({
            isNewVehicleValid: true,
        });
    };

    _getUserVehicles({
        user,
        showUnknownChoices,
        iDontKnowChecked,
        vehicleId,
        make,
        model,
        year,
    }) {
        const {isNewVehicle} = this.props;
        const showAddNew = !(
            (user.vehicles && user.vehicles.length > 0) ||
            iDontKnowChecked
        );
        const vehicles = user.vehicles ? user.vehicles : [];
        let newVehicleId = vehicleId;
        let newMake = make;
        let newModel = model;
        let newYear = year;

        const initialVehicles = map(vehicles, vehicle => {
            if (!newVehicleId && vehicle.isDefault) {
                newVehicleId = vehicle.id;
            }

            return {
                label: `${vehicle.year} ${vehicle.makeLabel} ${vehicle.modelLabel}`,
                name: vehicle.name,
                value: vehicle.id.toString(),
                checked:
                    !isNewVehicle &&
                    newMake === vehicle.make &&
                    newModel === vehicle.model &&
                    newYear === vehicle.year,
                make: vehicle.make,
                model: vehicle.model,
                year: vehicle.year,
            };
        });

        if (showUnknownChoices) {
            initialVehicles.push({
                label: `I don't know my vehicle details right now`,
                value: 'DONTKNOW',
                checked: iDontKnowChecked,
                className: 'VehicleList-dont-know',
            });
        }

        initialVehicles.push({
            label: 'Add new vehicle',
            value: 'new',
            checked: showAddNew || isNewVehicle,
        });

        if (filter(initialVehicles, {checked: true}).length === 0) {
            // there are no checked vehicles, this may happen if the user deleted their vehicle in their account and its
            // still showing up on parking pass or similar so we select the first one available
            initialVehicles[0].checked = true;
            newVehicleId = initialVehicles[0].value;
            newMake = initialVehicles[0].make;
            newModel = initialVehicles[0].model;
            newYear = initialVehicles[0].year;
        }

        return {
            vehicleId: newVehicleId,
            make: newMake,
            model: newModel,
            year: newYear,
            vehicles: initialVehicles,
            showAddNew,
        };
    }

    _loadOversizedFee({make, model, year}, hideShowNew = true) {
        const {facilityId} = this.props;
        const newState = {
            feeMessageLoading: false,
        };

        this.setState({
            make,
            model,
            year,
            feeMessageLoading: true,
            feeError: null,
            showAddNew: !hideShowNew,
            showWarningTooltip: false,
            requiredError: null,
        });

        this._feeLoader = APIUtils.makeCancelable(
            OversizedFeeAPI.getOversizedFee({facilityId, make, model, year})
        );
        this._feeLoader.promise
            .then(response => {
                const {
                    fee,
                    message,
                    not_allowed: notAllowed,
                } = response.data.data.results;

                newState.fee = fee ? fee : 0;
                newState.feeMessage = message
                    ? message
                    : 'No oversized fees apply for this vehicle.';
                newState.vehicleNotAllowed = notAllowed;

                this._oversizedFeeLoaded(response, newState);
            })
            .catch(response => {
                newState.feeError =
                    'We are unable to check for oversized fees at this time. Please continue to checkout and update your vehicle information before you park.';

                this._oversizedFeeLoaded(response, newState);
            });
    }

    _oversizedFeeLoaded(response, newState) {
        if (this._isUnmounted) {
            return;
        }

        const {onOversizedFeeUpdated} = this.props;

        this.setState(newState);

        if (onOversizedFeeUpdated) {
            onOversizedFeeUpdated({
                response,
                state: this.state,
            });
        }
    }

    _setFeeError = feeError => {
        this.setState({
            feeError,
            apiFailed: true,
        });
    };

    _renderDontKnowTooltip() {
        const {isParentVisible, isMobile} = this.props;
        const {showWarningTooltip} = this.state;

        if (!isParentVisible || !showWarningTooltip || isMobile) {
            return null;
        }

        return (
            <Tooltip
                className="VehicleList-dont-know-tooltip"
                position="right"
                triggerSelector=".VehicleList-dont-know"
                tipOffset={10}
                onHidden={this._onWarningTooltipHidden}
            >
                That&apos;s alright! Knowing your vehicle details before
                entering the garage can help with conversations about the
                oversize fees at the garage.
            </Tooltip>
        );
    }

    _renderOversizedFeeMessage() {
        const {feeMessage, defaultFeeMessage, vehicleNotAllowed} = this.state;

        if (feeMessage && !isEmpty(feeMessage)) {
            return (
                <div className="content">
                    <strong>
                        {feeMessage}
                        {!vehicleNotAllowed &&
                            feeMessage !==
                                'No oversized fees apply for this vehicle.' &&
                            ' due to the garage.'}
                    </strong>
                </div>
            );
        } else {
            return (
                <div className="content">
                    <strong>Vehicle not found</strong>{' '}
                    <small>
                        {defaultFeeMessage.replace(/<br>/g, '. ')} may be
                        charged at the spot.
                    </small>
                </div>
            );
        }
    }

    _renderFeeMessageLoader() {
        const {feeMessageLoading, isLoadingRates} = this.state;

        if (!feeMessageLoading && !isLoadingRates) {
            return null;
        }

        return (
            <div className="VehicleList-message">
                <Loader size={20} borderWidth={2} />
            </div>
        );
    }

    _renderOversizedFee() {
        const {
            fee,
            feeMessageLoading,
            vehicleNotAllowed,
            feeError,
        } = this.state;

        return (
            <Fragment>
                {!fee && feeError && !feeMessageLoading && <p>{feeError}</p>}
                {(fee || fee === 0) && !feeMessageLoading && (
                    <div className="VehicleList-message">
                        <Alert
                            size="lg"
                            type={vehicleNotAllowed ? 'danger' : 'muted'}
                        >
                            {this._renderOversizedFeeMessage()}
                        </Alert>
                    </div>
                )}
            </Fragment>
        );
    }

    _renderIncludedOversizedFee() {
        const {oversizeFee, currencyType} = this.props;
        const {vehicleId, isNewVehicleValid} = this.state;
        const noOversizeFeesApply =
            (isNewVehicleValid || vehicleId) && !oversizeFee;
        const classes = classNames('VehicleList-oversize-message', {
            'VehicleList-oversize-message-no-fee': noOversizeFeesApply,
        });

        return (
            <div className={classes}>
                {noOversizeFeesApply ? (
                    <IconCheck className="Icon-check" />
                ) : (
                    <IconExclamationCircle />
                )}
                {(vehicleId || isNewVehicleValid) && oversizeFee && (
                    <p>
                        Your vehicle has at least one oversize dimension. An
                        oversize vehicle fee of{' '}
                        <Price
                            amount={oversizeFee.price}
                            currencyType={currencyType}
                        />{' '}
                        will be included in your price.
                    </p>
                )}
                {!vehicleId && !isNewVehicleValid && (
                    <p>
                        This facility charges oversize vehicle fees. You could
                        be charged an oversize fee on site if you are driving a
                        larger vehicle. Please select your vehicle above to
                        check oversize fees.
                    </p>
                )}
                {noOversizeFeesApply && (
                    <p>No oversized fees apply for this vehicle.</p>
                )}
            </div>
        );
    }

    _renderVehicleList() {
        const {
            isMobile,
            user,
            required,
            newVehicleFormErrors,
            checkOversized,
            showSaveAsDefault,
            allowManualEntry,
            supportsOversizeFeeType,
            isLoadingRates,
            oversizeFee,
        } = this.props;
        const {edit, showAddNew, vehicles, vehicleId} = this.state;

        if (edit) {
            return (
                <Fragment>
                    {vehicles.length > 1 && (
                        <Fragment>
                            <Label>Vehicle</Label>
                            {checkOversized && (
                                <Alert type="highlight">
                                    This spot may charge fees onsite for
                                    oversized vehicles. Enter vehicle details to
                                    see estimated fees.
                                </Alert>
                            )}
                            <FormGroup>
                                <RadioGroup
                                    id="VehicleList-vehicle-options"
                                    name="vehicle"
                                    required={required}
                                    error={
                                        <FormElementError>
                                            {this._requiredErrorMessage}
                                        </FormElementError>
                                    }
                                    onChange={this._onVehicleChange}
                                >
                                    {map(vehicles, (vehicle, i) => {
                                        return (
                                            <Radio
                                                key={i}
                                                label={
                                                    <Label
                                                        className={
                                                            vehicle.className
                                                        }
                                                    >
                                                        {vehicle.name && (
                                                            <span className="Radio-item-name">
                                                                {vehicle.name}
                                                            </span>
                                                        )}
                                                        {vehicle.label}
                                                    </Label>
                                                }
                                                value={vehicle.value}
                                                defaultChecked={vehicle.checked}
                                            />
                                        );
                                    })}
                                </RadioGroup>
                            </FormGroup>
                        </Fragment>
                    )}
                    {showAddNew && (
                        <FormGroup>
                            <AddNewVehicleForm
                                ref={node => {
                                    this._newVehicleForm = node;
                                }}
                                user={user}
                                formErrors={newVehicleFormErrors}
                                onSelectChange={this._onNewVehicleSelectChange}
                                onVehicleDataRetrieved={
                                    this._onVehicleDataRetrieved
                                }
                                setOversizeFeeError={this._setFeeError}
                                showSaveAsDefault={showSaveAsDefault}
                                useVehicleSelectLabels={!isMobile}
                                allowManualEntry={allowManualEntry}
                            />
                        </FormGroup>
                    )}
                    {supportsOversizeFeeType &&
                        (oversizeFee || !vehicleId) &&
                        !isLoadingRates &&
                        this._renderIncludedOversizedFee()}
                    {checkOversized && this._renderOversizedFee()}
                    {(checkOversized || isLoadingRates) &&
                        this._renderFeeMessageLoader()}
                    {!supportsOversizeFeeType && this._renderDontKnowTooltip()}
                </Fragment>
            );
        } else {
            return (
                <Alert className="VehicleList-callout" size="md">
                    This spot may charge oversize fees.{' '}
                    <TextButton onClick={this._onEditClick}>
                        Does this apply to me?
                    </TextButton>
                </Alert>
            );
        }
    }

    submit() {
        const {onIDontKnowChanged} = this.props;
        const {
            showAddNew,
            iDontKnowChecked,
            vehicleId,
            make,
            model,
            year,
            apiFailed,
        } = this.state;

        if (apiFailed) {
            return true;
        }

        let newFormData = null;
        let newVehicleId = vehicleId;
        let newMake = make;
        let newModel = model;
        let newYear = year;
        let saveVehicleAsDefault = false;

        if (showAddNew) {
            newFormData = this._newVehicleForm.submit();
            newVehicleId = parseInt(newFormData.id, 10);
            newMake = newFormData.make;
            newModel = newFormData.model;
            newYear = newFormData.year;
            saveVehicleAsDefault = newFormData.saveVehicleAsDefault;
        }

        if (!(newMake && newModel && newYear) && !iDontKnowChecked) {
            this.setState({
                requiredError: this._requiredErrorMessage,
            });

            return false;
        }

        if (onIDontKnowChanged) {
            onIDontKnowChanged(iDontKnowChecked);
        }

        return {
            vehicleId: newVehicleId,
            vehicleMake: newMake,
            vehicleModel: newModel,
            vehicleYear: newYear,
            saveVehicleAsDefault,
        };
    }

    render() {
        const {highlight, requiredError, vehicles} = this.state;
        const classes = classNames(
            'VehicleList',
            {'VehicleList-no-vehicles': vehicles.length === 1},
            {highlight},
            {ContainsErrors: requiredError}
        );

        return (
            <Fragment>
                <FormGroup className={classes}>
                    {this._renderVehicleList()}
                </FormGroup>
                {requiredError && (
                    <FormGroupError>{requiredError}</FormGroupError>
                )}
            </Fragment>
        );
    }
}

export default withConfig(['isMobile'])(VehicleList);
